/**
 * Per-mode and per-tier photo caps for a single analysis request.
 *
 * Two independent axes:
 *  - MODE_PHOTO_CEILING is the absolute prompt/cost ceiling. Weed's 3 predates
 *    the tier ladder (one overview + two supporting views, see
 *    lib/analysis/weed-photo-accumulation.ts); identify/diagnose use
 *    MAX_ANALYSIS_IMAGES, which used to live inline in the analyze route.
 *  - TIER_PHOTO_CAPS is the monetized ladder: anonymous 1, registered free 3,
 *    premium 5. It governs rescans (the post-result refinement lane). A fresh
 *    capture reads up to CAPTURE_PHOTO_CAP for every tier.
 *
 * Weed is deliberately exempt from the tier ladder. Its accumulation loop ships
 * free to anonymous users (mobile/app/scan/weed.tsx addNextPhoto) with copy
 * promising exactly that, so applying the anonymous cap of 1 there would brick
 * a shipped flow. The tier ladder therefore governs identify and diagnose only.
 *
 * This is a different axis from the monthly analysis-count ladder in
 * lib/auth/feature-flags.ts. Do not conflate the two.
 */

import type { AnalysisMode } from '@/lib/types/diagnosis'

export type PhotoTier = 'anonymous' | 'registered_free' | 'premium'

export const MAX_ANALYSIS_IMAGES = 5

/** Absolute per-mode ceiling, independent of who is asking. */
export const MODE_PHOTO_CEILING: Record<AnalysisMode, number> = {
  identify: MAX_ANALYSIS_IMAGES,
  diagnose: MAX_ANALYSIS_IMAGES,
  weed: 3,
}

export const TIER_PHOTO_CAPS: Record<PhotoTier, number> = {
  anonymous: 1,
  registered_free: 3,
  premium: 5,
}

/**
 * Photos any caller may send on a fresh capture: the primary shot plus up to
 * two extra angles taken on the camera before the first result. Tier-blind on
 * purpose. A fresh capture always spends one scan from the caller's monthly
 * allowance (one request, one slot, however many photos), so the extra angles
 * buy accuracy on a scan that is already paid for, never a free re-run.
 *
 * Rescans and recoveries do NOT get this: re-running a result with more photos
 * is the post-result refinement lane, which stays behind TIER_PHOTO_CAPS and
 * the per-root additive allowance. See
 * obsidian/decisions/2026-09-28-multi-photo-scan-reversal.md.
 */
export const CAPTURE_PHOTO_CAP = 3

export function resolvePhotoCap(input: {
  mode: AnalysisMode
  tier: PhotoTier
  /** RATE_LIMIT_ALLOWLIST IPs test on prod against the raw ceiling. */
  allowlisted?: boolean
  /**
   * A fresh analysis: no rescanOfAnalysisId and no recovery source. Derived
   * server-side from the request body shape, never from a client header.
   */
  freshCapture?: boolean
}): number {
  const ceiling = MODE_PHOTO_CEILING[input.mode] ?? MODE_PHOTO_CEILING.identify
  if (input.mode === 'weed') return ceiling
  if (input.allowlisted) return ceiling
  const tierCap = Math.min(ceiling, TIER_PHOTO_CAPS[input.tier])
  if (input.freshCapture) return Math.max(tierCap, Math.min(ceiling, CAPTURE_PHOTO_CAP))
  return tierCap
}

/**
 * Inline byte budget for one analysis. Uploads are re-encoded to at most 5MB
 * each (lib/server-image-sanitizer.ts), so a real 3-photo capture sits far
 * below these. They bound what a caller can make us download and forward to
 * the vision model when it points several URLs at oversized storage objects.
 */
export const MAX_ANALYSIS_IMAGE_BYTES = 10 * 1024 * 1024
export const MAX_ANALYSIS_TOTAL_IMAGE_BYTES = 20 * 1024 * 1024

/** Decoded byte length of a base64 data URL, or null for a plain URL. */
export function dataUrlByteLength(input: string): number | null {
  if (!input.startsWith('data:')) return null
  const comma = input.indexOf(',')
  if (comma < 0) return null
  const base64Length = input.length - comma - 1
  const padding = input.endsWith('==') ? 2 : input.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((base64Length * 3) / 4) - padding)
}

export type ImageBudgetVerdict =
  | { ok: true; totalBytes: number }
  | { ok: false; reason: 'image_too_large' | 'images_too_large'; totalBytes: number }

/**
 * Checks the downloaded analysis inputs against the byte budget. Inputs that
 * failed to inline (plain URLs) are counted as unknown and pass, which keeps
 * the existing degrade-to-URL behavior for a transient storage error.
 */
export function checkAnalysisImageBudget(inputs: readonly string[]): ImageBudgetVerdict {
  let totalBytes = 0
  for (const input of inputs) {
    const bytes = dataUrlByteLength(input)
    if (bytes === null) continue
    if (bytes > MAX_ANALYSIS_IMAGE_BYTES) return { ok: false, reason: 'image_too_large', totalBytes: totalBytes + bytes }
    totalBytes += bytes
  }
  if (totalBytes > MAX_ANALYSIS_TOTAL_IMAGE_BYTES) return { ok: false, reason: 'images_too_large', totalBytes }
  return { ok: true, totalBytes }
}

/**
 * The lowest tier that could send `requested` photos for this mode, or null
 * when even premium cannot. Drives the routing fields on the 403 body so the
 * clients know whether to open sign-in or the paywall.
 */
export function tierRequiredForPhotoCount(
  mode: AnalysisMode,
  requested: number,
): PhotoTier | null {
  const ceiling = MODE_PHOTO_CEILING[mode] ?? MODE_PHOTO_CEILING.identify
  if (requested <= TIER_PHOTO_CAPS.anonymous) return 'anonymous'
  if (requested <= Math.min(ceiling, TIER_PHOTO_CAPS.registered_free)) return 'registered_free'
  if (requested <= ceiling) return 'premium'
  return null
}

/**
 * Why the 403 fired. Both denials share `code: 'photo_limit_reached'`, but they
 * are different stories: 'photo_count' means this request carried more photos
 * than the tier reads at once, while 'additive_cap' means the per-root free
 * refinement allowance is spent. Clients pick their copy from this, so a spent
 * refinement never renders as "you sent 3 views and your plan reads 3".
 * Optional on the type because older payloads carry no reason.
 */
export type PhotoLimitReason = 'photo_count' | 'additive_cap'

/** Wire shape of the 403 `photoLimit` object. Shared by both clients. */
export interface PhotoLimitDetail {
  cap: number
  requested: number
  tier: PhotoTier
  mode: AnalysisMode
  requiredTier: PhotoTier | null
  reason?: PhotoLimitReason
}
